import React, { useEffect, useState, useCallback } from 'react';
import { Grid, Tabs, Tab } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as actions from '../../../../store/index';
import { fetchGet, fetchPut } from '../../../../helpers/fetching';
import DraggableDialog from '../../Dialogs/DraggableDialog/DraggableDialog';
import Other from './Other/Other';
import Processing from './Processing/Processing';
import { setSnackbar, setInfo, fetchGlobalSettings } from '../../../../store';
import Personalization from './Personalization/Personalization';
import OptedOutPatients from './OptedOutPatients/OptedOutPatients';
import OptingOutOperators from './OptingOutOperators/OptingOutOperators';

const useStyles = makeStyles((theme) => ({
  Dialog: {
    '& .MuiDialog-paper': {
      minHeight: '65vh',
    },
  },

  tabs: {
    borderRight: `1px solid ${theme.palette.divider}`,
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    color: theme.palette.secondary.main,
    alignItems: 'center',
  },
}));

const initialSettings = {
  filter_dcm: false,
  anonymize: false,
  store_dicoms: false,
  discFree: 0,
  discSize: 0,
  dicomCount: 0,
  presentation_lesions: '',
  presentation_quality: '',
  remove_after_days: null,
  inverted_view_position: false,
  crop_to_breast: false,
};

function toUserSettingsPutBody(settings) {
  return {
    filter_dcm: settings.filter_dcm,
    anonymize: settings.anonymize,
    store_dicoms: settings.store_dicoms,
    presentation_lesions: settings.presentation_lesions,
    presentation_microcalc: settings.presentation_microcalc,
    presentation_parenchyma: settings.presentation_quality || 'contours',
    presentation_folds: settings.presentation_folds,
    presentation_blur: settings.presentation_blur,
    presentation_pectoralis: settings.presentation_pectoralis,
    remove_after_days: settings.remove_after_days,
    inverted_view_position: settings.inverted_view_position,
    crop_to_breast: !!settings.crop_to_breast,
    priors_number_limit: settings.priors_number_limit,
    priors_years_limit: settings.priors_years_limit,
    priors_report: settings.priors_report,
  };
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {children}
    </div>
  );
}

function Settings(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const currentRole = localStorage.getItem('role');
  const global_settings = useSelector((state) => state.global_settings);
  const currentExam = useSelector((state) => state.examinations.currentExam);

  // State
  const [dicomServers, setDicomServers] = useState([]);
  const [serverActive, setServerActive] = useState(false);
  const [watcherActive, setWatcherActive] = useState(false);
  const [settings, setSettings] = useState(initialSettings);
  const [settingsChanged, setSettingsChanged] = useState(false);
  const [serversChanged, setServerChanged] = useState(false);
  const [anonymized, setAnonimized] = useState(0);
  const [value, setValue] = useState(0);
  const [loadingOrthanc, setLoadingOrthanc] = useState(false);
  const [loadingWatcher, setLoadingWatcher] = useState(false);
  const activeDialog = useSelector((state) => state.gui.activeDialog);

  const managementAccess = ['admin', 'supervisor', 'radiologist', 'service'].includes(currentRole);
  const serviceAccess = ['admin', 'service'].includes(currentRole);

  // Fetching
  const fetchSettings = useCallback(async () => {
    const response = await fetchGet('settings');
    if (response.success) {
      response.data.presentation_quality = response.data.presentation_parenchyma || 'contours';
      response.data.presentation_lesions = response.data.presentation_lesions || 'boxes';
      if (response.data.presentation_lesions.endsWith('_boxes')) {
        response.data.presentation_lesions = response.data.presentation_lesions.replace('_boxes', '');
      }

      const { report_configuration: _report, ...userSettings } = response.data;
      setSettings(userSettings);
      setAnonimized(response.data.anonymize);
    } else
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        }),
      );
  }, [dispatch]);

  const fetchOrthancState = useCallback(async () => {
    const response = await fetchGet('orthanc', 'bbox');
    if (response.success) setServerActive(response.data);
    else
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        }),
      );
  }, [dispatch]);

  const fetchWatcherState = useCallback(async () => {
    const response = await fetchGet('watcher', 'bbox');
    if (response.success) setWatcherActive(response.data);
    else
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        }),
      );
  }, [dispatch]);

  // Effects
  useEffect(() => {
    fetchSettings();
    fetchOrthancState();
    fetchWatcherState();
  }, [fetchSettings, fetchOrthancState, fetchWatcherState]);

  // Handlers
  function addDicomServer(newServer) {
    const newServerList = dicomServers.filter((s) => s.Name !== newServer.Name);
    newServerList.push(newServer);
    setDicomServers(newServerList);
    setServerChanged(true);
  }

  const updateSettings = (name, value) => {
    setSettings((prevSettings) => ({ ...prevSettings, [name]: value }));
    setSettingsChanged(true);
  };

  function updateSettingsCheckbox(event) {
    const { checked, name } = event.target;
    updateSettings(name, checked ? 1 : 0);
  }

  function updateSettingsValue(event) {
    let { value, name } = event.target;
    if (value) {
      value = parseInt(value);
      if (!Number.isInteger(value)) return;
    }
    updateSettings(name, value);
  }

  function updateSettingsText(event) {
    const { value, name } = event.target;
    updateSettings(name, value);
  }

  const updatePresentationDisplay = (event, settingKey) => {
    const { value, type, checked } = event.target;
    setSettings((prevSettings) => {
      const newSettings = { ...prevSettings };
      if (type === 'radio') {
        newSettings[settingKey] = value;
      } else if (type === 'checkbox') {
        const suffix = checked ? '_boxes' : '';
        const baseValue = prevSettings[settingKey].replace(/_boxes$/, '');
        newSettings[settingKey] = `${baseValue}${suffix}`;
      }
      return newSettings;
    });
    setSettingsChanged(true);
  };

  const updateQualityPresentationDisplay = (event) =>
    updatePresentationDisplay(event, 'presentation_quality');
  const updateDiagnosticsPresentationDisplay = (event) =>
    updatePresentationDisplay(event, 'presentation_lesions');

  // Save and set new settings
  async function putSettings() {
    const body = {
      ...toUserSettingsPutBody(settings),
      loggedUser: localStorage.getItem('username'),
    };
    let response = await fetchPut('settings', body);
    if (response.success) {
      dispatch(
        setSnackbar({
          msg: 'Settings.snack_settings_saved',
          severity: 'success',
        }),
      );

      if (anonymized !== settings.anonymize) {
        dispatch(actions.fetchExaminations());
      }

      dispatch(fetchGlobalSettings());
    } else
      dispatch(
        setSnackbar({
          msg: 'Settings.error_saving',
          severity: 'error',
        }),
      );
  }

  async function putServers() {
    const response = await fetchPut('dicom_servers', dicomServers);
    if (response.success)
      dispatch(
        setSnackbar({
          msg: 'Settings.snack_dicom_saved',
          severity: 'success',
        }),
      );
    else
      dispatch(
        setSnackbar({
          msg: 'Settings.snack_dicom_failed',
          severity: 'error',
        }),
      );
  }

  async function setOrthanc(event) {
    setLoadingOrthanc(true);
    const { checked } = event.target;
    const response = await fetchPut('orthanc', { state: checked }, 'bbox');
    if (response.success) {
      setServerActive(checked);
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'success',
        }),
      );
      setLoadingOrthanc(false);
    } else {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        }),
      );
      setLoadingOrthanc(false);
    }
  }

  async function setWatcher(event) {
    setLoadingWatcher(true);
    const isActive = event.target.checked;
    const response = await fetchPut('watcher', { state: isActive }, 'bbox');
    if (response.success) {
      setWatcherActive(isActive);
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'success',
        }),
      );
      setLoadingWatcher(false);
    } else {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        }),
      );
      setLoadingWatcher(false);
    }
  }

  function saveChanges(_) {
    if (settingsChanged) putSettings();

    if (serversChanged) putServers();

    setSettingsChanged(false);
    setServerChanged(false);

    dispatch(actions.setActiveDialog(null));
  }

  function onClose(_) {
    dispatch(actions.setActiveDialog(null));
  }

  return (
    <DraggableDialog
      className={classes.Dialog}
      maxWidth='lg'
      title={t('Settings.settings')}
      open={activeDialog === 'settings'}
      style={{ zIndex: theme.zIndex.main_drawer }}
      actions={[
        {
          onClick: saveChanges,
          color: 'primary',
          label: t('Settings.save_settings'),
        },
      ]}
      onClose={onClose}>
      <Grid container direction='row' justifyContent='space-evenly' alignItems='flex-start' spacing={1}>
        <Grid item xs={2}>
          <Tabs
            className={classes.tabs}
            orientation='vertical'
            value={value}
            onChange={(_, newVal) => setValue(newVal)}>
            <Tab label={t('Settings.personalization')} id='personalization' />
            <Tab label={t('Settings.processing')} id='processing' />
            {managementAccess && <Tab label={t('Settings.other')} id='other' />}
            {managementAccess && <Tab label={t('Settings.optingOut')} id='optingOut' />}
          </Tabs>
        </Grid>

        <Grid item xs={10}>
          <TabPanel value={value} index={0}>
            <Personalization
              settings={settings}
              updateSettingsCheckbox={updateSettingsCheckbox}
              updateSettingsValue={updateSettingsValue}
              managementAccess={managementAccess}
              updateQualityPresentationDisplay={updateQualityPresentationDisplay}
              updateDiagnosticsPresentationDisplay={updateDiagnosticsPresentationDisplay}
              updateSettings={updateSettings}
            />
          </TabPanel>
          <TabPanel value={value} index={1}>
            <Processing
              loadingOrthanc={loadingOrthanc}
              loadingWatcher={loadingWatcher}
              serverActive={serverActive}
              setOrthanc={setOrthanc}
              setWatcher={setWatcher}
              watcherActive={watcherActive}
              managementAccess={serviceAccess}
            />
          </TabPanel>

          <TabPanel value={value} index={2}>
            <Other
              settings={settings}
              updateSettingsCheckbox={updateSettingsCheckbox}
              updateSettingsValue={updateSettingsValue}
              managementAccess={managementAccess}
            />
          </TabPanel>

          <TabPanel value={value} index={3}>
            <Grid container direction='column' spacing={2}>
              <Grid item xs={12}>
                <OptedOutPatients />
              </Grid>
              <Grid item xs={12}>
                <OptingOutOperators />
              </Grid>
            </Grid>
          </TabPanel>
        </Grid>
      </Grid>
    </DraggableDialog>
  );
}

export default Settings;
